import React, { FunctionComponent, MouseEvent } from 'react';
import { TableColumn, Button, RowActionsRender, Colors } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { IoIosAddCircleOutline } from 'react-icons/io';

// utils
import { unescapeText } from 'utils/helpers';

// types
import AgencyState, { Follower } from 'ducks/merchantDucks/agency/types';

// components
import TableActionMenu from 'components/Table/TableActionMenu';
import GenericTable from 'components/Table/GenericTable';
import CommonIcon from 'components/CommonIcon';

const {
  ActionMenuContainer,
  ActionMenuContent,
  ActionMenuDropdown,
  ActionMenuDropdownItem,
  ActionMenuLink,
} = TableActionMenu;

const Image = styled.img`
  height: 30px;
  width: 30px;
  border-radius: 3px;
  border: 1px solid ${Colors.skyBlue};
  vertical-align: middle;
`;

interface OwnProps {
  followers: AgencyState;
  paginationPage: number;
  handlePageClick: (pageNumber: number) => any;
  handleAddClient: () => any;
  handleUpdateExistingMerchant: () => any;
  openImpersonateModal?: (follower: Follower) => any;
  handleManageInheritance?: (follower: Follower) => any;
  openDeleteModal?: (id: number) => any;
  renderHeaderSection: () => Node | JSX.Element;
}

type Props = OwnProps;

type FollowerActionHandler = (follower: Follower) => (e: MouseEvent<HTMLElement>) => void;

const ClientAccountsTable: FunctionComponent<Props> = ({
  openImpersonateModal,
  handleManageInheritance,
  openDeleteModal,
  followers,
  handleAddClient,
  handleUpdateExistingMerchant,
  paginationPage,
  handlePageClick,
  renderHeaderSection,
}) => {
  const handleOpenImpersonateModal: FollowerActionHandler = (follower) => (e) => {
    e.preventDefault();
    if (openImpersonateModal) {
      openImpersonateModal(follower);
    }
  };

  const handleEditFollower: FollowerActionHandler = (follower) => (e) => {
    e.preventDefault();
    if (handleManageInheritance) {
      handleManageInheritance(follower);
    }
  };

  const handleDeleteFollower = (id: number) => (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (openDeleteModal) {
      openDeleteModal(id);
    }
  };

  const generateColumns = (): Array<TableColumn<Follower>> => [
    {
      title: '',
      key: 'avatar_url',
      render: ({ value }) => {
        const imageSrc = value || 'https://assets.inplayer.com/images/avatar.jpg';
        return <Image alt="avatar-img" src={imageSrc} />;
      },
    },
    {
      title: 'Name',
      key: 'full_name',
      render: ({ value }) => unescapeText(value),
    },
    {
      title: 'Email',
      key: 'email',
    },
    {
      title: '',
      key: '',
      render: ({ rowValues }) => (
        <Button tag="button_login_as" type="button" onClick={handleOpenImpersonateModal(rowValues)}>
          Login as
        </Button>
      ),
    },
  ];

  const renderRowActions: RowActionsRender<Follower> = ({ row }) => (
    <ActionMenuContainer>
      <ActionMenuContent>
        <ActionMenuDropdown>
          <ActionMenuLink href="#/" onClick={handleEditFollower(row)}>
            <ActionMenuDropdownItem>Manage inheritance</ActionMenuDropdownItem>
          </ActionMenuLink>
          <ActionMenuLink href="#/" onClick={handleDeleteFollower(row.id)}>
            <ActionMenuDropdownItem>Remove account</ActionMenuDropdownItem>
          </ActionMenuLink>
        </ActionMenuDropdown>
      </ActionMenuContent>
      <CommonIcon name="settings" hoverStatus="settings" />
    </ActionMenuContainer>
  );

  const { total, size } = followers;

  return (
    <GenericTable<Follower>
      type="clients"
      hasBottomPagination
      data={followers.collection}
      columns={generateColumns()}
      total={total}
      currentPage={paginationPage}
      handlePageClick={handlePageClick}
      pageRangeDisplayed={3}
      itemsPerPage={size}
      showLoader={followers.isFetching}
      options={{
        rowActions: renderRowActions,
        headerSection: renderHeaderSection(),
      }}
      tableButton={[
        {
          label: 'Create new related account',
          icon: <IoIosAddCircleOutline />,
          onClick: handleAddClient,
          type: 'button',
          // tag: 'button_new_create_a_new_related_account'
        },
        {
          label: 'Update existing merchant',
          icon: <IoIosAddCircleOutline />,
          onClick: handleUpdateExistingMerchant,
          type: 'button',
          // tag: 'button_update_update_existing_merchant'
        },
      ]}
      renderEmptyTable
      actionsRowTitle="Edit"
    />
  );
};

export default ClientAccountsTable;
